import type {
  IPrizePopCampaign,
  PrizePopFormula,
  IPrizePopPrize,
  IPrizePopWin,
  PrizePopInterval,
  PrizePopWinsResponse,
  CampaignTypes,
  PrizePopStatuses,
} from '../types/campaign.types';
import { PRIZE_POP_CAMPAIGN_TYPE, CAMPAIGN_LEVELS } from '../types/campaign.types';

import { buildPortalAssetUrl } from 'shared/utils/helpers';

export const getCarouselTopper = (campaignType: CampaignTypes = PRIZE_POP_CAMPAIGN_TYPE) =>
  buildPortalAssetUrl(`/campaigns/icons/topper_${campaignType}.webp`);

/**
 * Generates mock campaign intervals with configurable delay
 * @param delaySeconds - Delay in seconds before the first interval starts
 * @param intervalDuration - Duration of each interval in seconds
 * @returns Array of campaign intervals
 */
export const generateMockIntervals = (delaySeconds: number = 10, intervalDuration: number = 10): PrizePopInterval[] => {
  const now = new Date();

  const startTime1 = new Date(now.getTime() + delaySeconds * 1000);
  const endTime1 = new Date(startTime1.getTime() + intervalDuration * 1000);

  const startTime2 = new Date(endTime1.getTime() + intervalDuration * 1000);
  const endTime2 = new Date(startTime2.getTime() + intervalDuration * 1000);

  return [
    {
      range: {
        startRangeDate: startTime1.toISOString(),
        endRangeDate: endTime1.toISOString(),
      },
      hoursRange: [
        {
          startTime: startTime1.toISOString(),
          endTime: endTime1.toISOString(),
        },
      ],
    },
    {
      range: {
        startRangeDate: startTime2.toISOString(),
        endRangeDate: endTime2.toISOString(),
      },
      hoursRange: [
        {
          startTime: startTime2.toISOString(),
          endTime: endTime2.toISOString(),
        },
      ],
    },
  ];
};

const GAME_CARD_ICON_MAP: Record<CampaignTypes, string> = {
  prize_pop: '/campaigns/icons/PrizePopGameCardIcon',
  tournaments: '/campaigns/icons/TournamentsGameCardIcon',
  rebate: '/campaigns/icons/RebateGameCardIcon',
};

export const getCampaignGameCardIcon = (campaignType: CampaignTypes = PRIZE_POP_CAMPAIGN_TYPE) =>
  GAME_CARD_ICON_MAP[campaignType];

export const mockCampaignData: IPrizePopCampaign = {
  id: '1',
  name: 'Holiday Season Campaign 2222222222222222222222222222222222',
  terms: 'Play eligible games during the campaign period to win instant prizes. Minimum bet requirements apply.',
  status: 'PROMO' as PrizePopStatuses,
  startDate: '2025-11-10T22:00:00Z',
  endDate: '2025-11-29T22:00:00Z',
  visibleEndDate: '2025-11-29T22:10:00Z',
  prizeFund: 300000000000,
  level: CAMPAIGN_LEVELS.GOLD,
  games: [
    'FYHBSlot',
    'mega-moolah',
    'SACBLSlot',
    'BCSlot',
    'TNCLHSlot',
    'gonzos-quest',
    'GAGKLVSlot',
    'book-of-dead',
    'SCWMSlot',
    'OHSHSlot',
  ],
  formula: 'RANDOM' as PrizePopFormula,
  formulaParams: null,
  minSpinNumber: 12,
  minStake: 123,
  baseCurrency: 'EUR',
  joined: false,
  type: PRIZE_POP_CAMPAIGN_TYPE,
  resources: {
    get CampaignsMultiViewSoloTransparentImage() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Branded/CampaignsMultiViewSoloTransparentImage.webp');
    },
    get CampaignsMultiViewSoloTransparentImageDesktop() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Branded/CampaignsMultiViewSoloTransparentImageDesktop.webp');
    },
    get CampaignsMultiViewSoloSeparatedBackgroundLandscape() {
      return buildPortalAssetUrl(
        '/campaigns/prize_pop/Branded/CampaignsMultiViewSoloSeparatedBackgroundLandscape.webp',
      );
    },
    get CampaignsMultiViewSoloSeparatedBackgroundPortrait() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Branded/CampaignsMultiViewSoloSeparatedBackgroundPortrait.webp');
    },

    get CampaignsSingleViewSoloBackgroundLandscape() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Branded/CampaignsSingleViewSoloBackgroundLandscape.webp');
    },
    get CampaignsSingleViewSoloBackgroundPortrait() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Branded/CampaignsSingleViewSoloBackgroundPortrait.webp');
    },
    get CampaignsSingleViewSoloBackgroundClose() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Branded/CampaignsSingleViewSoloBackgroundClose.webp');
    },
    get CampaignsPlateCloseLogo() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Branded/CampaignsPlateCloseLogo.webp');
    },

    get CampaignsPlate() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Branded/CampaignsPlate.webp');
    },
    get CampaignsPlateClose() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Branded/CampaignsPlateClose.webp');
    },
    get FillColor() {
      return '#3F0F6B';
    },
  },
  intervals: [
    {
      range: {
        startRangeDate: '2025-06-13T00:00:00Z',
        endRangeDate: '2025-06-13T23:59:59Z',
      },
      hoursRange: [
        { startTime: '08:00:00', endTime: '11:00:00' },
        { startTime: '19:00:00', endTime: '20:00:00' },
      ],
    },
    {
      range: {
        startRangeDate: '2025-06-14T00:00:00Z',
        endRangeDate: '2025-06-30T23:59:59Z',
      },
      hoursRange: [
        { startTime: '08:00:00', endTime: '11:00:00' },
        { startTime: '19:00:00', endTime: '20:00:00' },
        { startTime: '22:20:00', endTime: '23:20:00' },
      ],
    },
    {
      range: {
        startRangeDate: '2025-07-01T00:00:00Z',
        endRangeDate: '2025-07-01T23:59:59Z',
      },
      hoursRange: [
        { startTime: '10:00:00', endTime: '12:00:00' },
        { startTime: '14:00:00', endTime: '16:00:00' },
        { startTime: '18:00:00', endTime: '21:00:00' },
      ],
    },
    {
      range: {
        startRangeDate: '2025-07-05T00:00:00Z',
        endRangeDate: '2025-07-15T23:59:59Z',
      },
      hoursRange: [
        { startTime: '09:00:00', endTime: '12:00:00' },
        { startTime: '15:00:00', endTime: '18:00:00' },
      ],
    },
    {
      range: {
        startRangeDate: '2025-07-20T00:00:00Z',
        endRangeDate: '2025-07-20T23:59:59Z',
      },
      hoursRange: [
        { startTime: '08:30:00', endTime: '10:30:00' },
        { startTime: '12:00:00', endTime: '14:00:00' },
        { startTime: '16:00:00', endTime: '18:00:00' },
        { startTime: '20:00:00', endTime: '22:00:00' },
      ],
    },
  ],

  joinedPlayersCount: 1247,
  totalPrizesCount: 150,
  prizesWonCount: 0,
};

export const mockCampaignData2: IPrizePopCampaign = {
  id: '2',
  name: 'Holiday Season Campaign Part 1 Holiday Season Campaign Part 1',
  terms: 'Special New Year campaign with increased prizes and extended play time.',
  status: 'PROMO' as PrizePopStatuses,
  startDate: '2026-01-01T00:00:00Z',
  endDate: '2026-01-31T23:59:59Z',
  visibleEndDate: '2026-02-01T12:00:00Z',
  prizeFund: 900000000000,
  level: CAMPAIGN_LEVELS.PURPLE,
  games: [
    'gonzos-quest',
    'SACBLSlot',
    'OHSHSlot',
    'TNCLHSlot',
    'book-of-dead',
    'BCSlot',
    'SCWMSlot',
    'mega-moolah',
    'FYHBSlot',
    'GAGKLVSlot',
  ],
  formula: 'RANDOM' as PrizePopFormula,
  formulaParams: null,
  minSpinNumber: 25,
  minStake: 50,
  baseCurrency: 'EUR',
  joined: true,
  type: PRIZE_POP_CAMPAIGN_TYPE,
  resources: {
    get CampaignsPlate() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Default/CampaignsPlate.webp');
    },
    get CampaignsSingleViewSoloBackgroundLandscape() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Default/CampaignsSingleViewSoloBackgroundLandscape.webp');
    },
    get CampaignsSingleViewSoloBackgroundPortrait() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Default/CampaignsSingleViewSoloBackgroundClose.webp');
    },
    get CampaignsPlateCloseLogo() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Default/CampaignsPlateCloseLogo.webp');
    },

    get CampaignsPlateClose() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Default/CampaignsPlateClose.webp');
    },
  },
  intervals: generateMockIntervals(15, 15),
  joinedPlayersCount: 856,
  totalPrizesCount: 200,
  prizesWonCount: 5,
};

export const mockCampaignDataMulti: IPrizePopCampaign = {
  id: '3',
  name: 'Christmas Prize Pop Campaign',
  terms: 'Play eligible games during the campaign period to win instant prizes. Minimum bet requirements apply.',
  status: 'PROMO' as PrizePopStatuses,
  startDate: '2025-11-10T22:00:00Z',
  endDate: '2025-11-29T22:00:00Z',
  visibleEndDate: '2025-11-29T22:10:00Z',
  prizeFund: 3000000,
  level: CAMPAIGN_LEVELS.GOLD,
  games: [
    'SCWMSlot',
    'book-of-dead',
    'GAGKLVSlot',
    'FYHBSlot',
    'mega-moolah',
    'OHSHSlot',
    'BCSlot',
    'SACBLSlot',
    'gonzos-quest',
    'TNCLHSlot',
  ],
  formula: 'RANDOM' as PrizePopFormula,
  formulaParams: null,
  minSpinNumber: 12,
  minStake: 123,
  baseCurrency: 'EUR',
  joined: true,
  type: PRIZE_POP_CAMPAIGN_TYPE,
  resources: {
    get CampaignsMultiViewSoloTransparentImage() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Default/CampaignsMultiViewSoloTransparentImage.webp');
    },
    get CampaignsMultiViewSoloTransparentImageDesktop() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Default/CampaignsMultiViewSoloTransparentImageDesktop.webp');
    },
    get CampaignsMultiViewSoloSeparatedBackgroundLandscape() {
      return buildPortalAssetUrl(
        '/campaigns/prize_pop/Default/CampaignsMultiViewSoloSeparatedBackgroundLandscape.webp',
      );
    },
    get CampaignsMultiViewSoloSeparatedBackgroundPortrait() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Default/CampaignsMultiViewSoloSeparatedBackgroundPortrait.webp');
    },

    get CampaignsSingleViewSoloBackgroundLandscape() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Default/CampaignsSingleViewSoloBackgroundLandscape.webp');
    },
    get CampaignsSingleViewSoloBackgroundPortrait() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Default/CampaignsSingleViewSoloBackgroundPortrait.webp');
    },
    get CampaignsSingleViewSoloBackgroundClose() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Default/CampaignsSingleViewSoloBackgroundClose.webp');
    },
    get CampaignsPlateCloseLogo() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Default/CampaignsPlateCloseLogo.webp');
    },

    get CampaignsPlate() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Default/CampaignsPlate.webp');
    },
    get CampaignsPlateClose() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Default/CampaignsPlateClose.webp');
    },
    get FillColor() {
      return '#3F0F6B';
    },
  },
  intervals: generateMockIntervals(260, 260),
  joinedPlayersCount: 1247,
  totalPrizesCount: 300,
  prizesWonCount: 12,
};

export const mockCampaignDataMulti2: IPrizePopCampaign = {
  id: '4',
  name: 'New Year Prize Pop Campaign',
  terms: 'Play eligible games during the campaign period to win instant prizes. Minimum bet requirements apply.',
  status: 'PROMO' as PrizePopStatuses,
  startDate: '2025-11-10T22:00:00Z',
  endDate: '2025-11-29T22:00:00Z',
  visibleEndDate: '2025-11-29T22:10:00Z',
  prizeFund: 3000000,
  level: CAMPAIGN_LEVELS.GOLD,
  games: [
    'TNCLHSlot',
    'SACBLSlot',
    'mega-moolah',
    'GAGKLVSlot',
    'BCSlot',
    'gonzos-quest',
    'OHSHSlot',
    'FYHBSlot',
    'book-of-dead',
    'SCWMSlot',
  ],
  formula: 'RANDOM' as PrizePopFormula,
  formulaParams: null,
  minSpinNumber: 12,
  minStake: 123,
  baseCurrency: 'EUR',
  joined: false,
  type: PRIZE_POP_CAMPAIGN_TYPE,
  resources: {
    get CampaignsPlate() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Branded/CampaignsPlate.webp');
    },
    get CampaignsMultiViewSoloSeparatedBackgroundLandscape() {
      return buildPortalAssetUrl(
        '/campaigns/prize_pop/MultiLobby/CampaignsMultiViewSoloSeparatedBackgroundLandscape.webp',
      );
    },
    get CampaignsMultiViewSoloSeparatedBackgroundPortrait() {
      return buildPortalAssetUrl(
        '/campaigns/prize_pop/MultiLobby/CampaignsMultiViewSoloSeparatedBackgroundPortrait.webp',
      );
    },
    get CampaignsMultiViewSoloTransparentImage() {
      return buildPortalAssetUrl('/campaigns/prize_pop/MultiLobby/CampaignsMultiViewSoloTransparentImage.webp');
    },
    get CampaignsMultiViewSoloTransparentImageDesktop() {
      return buildPortalAssetUrl('/campaigns/prize_pop/MultiLobby/CampaignsMultiViewSoloTransparentImageDesktop.webp');
    },
    get CampaignsPlateCloseLogo() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Default/CampaignsPlateCloseLogo.webp');
    },
    get CampaignsPlateClose() {
      return buildPortalAssetUrl('/campaigns/prize_pop/Branded/CampaignsPlateClose.webp');
    },
  },
  intervals: generateMockIntervals(25, 25),
  joinedPlayersCount: 1247,
  totalPrizesCount: 180,
  prizesWonCount: 8,
};

// Mock Campaign Wins
const mockCampaignWins: IPrizePopWin[] = [
  {
    name: 'test',
    casinoPlayerId: '123',
    casinoId: 3,
    prize: '12345',
    type: 'MONEY',
    currencyCode: 'EUR',
    date: '2025-11-10T22:00:00Z',
  },
  {
    name: 'yoankanchev',
    casinoPlayerId: '94515',
    casinoId: 3,
    prize: '123',
    type: 'MONEY',
    currencyCode: 'EUR',
    date: '2025-11-10T22:00:00Z',
  },
  {
    name: 'luckyplayer',
    casinoPlayerId: '78901',
    casinoId: 3,
    prize: '500',
    type: 'MONEY',
    currencyCode: 'EUR',
    date: '2025-11-09T15:30:00Z',
  },
];

export const mockPrizePopWinsResponse: PrizePopWinsResponse = {
  wins: mockCampaignWins,
  totalWins: 3,
};

export const getMockCampaignPrizesResponse: IPrizePopPrize[] = [
  {
    value: '1000',
    type: 'MONEY',
    currencyCode: 'EUR',
    count: 3,
    remaining: 2,
  },
];
